import {
	ArrowForward,
	Delete,
	Edit,
	FileDownload,
	Upload,
	Warning
} from '@mui/icons-material'
import Create from '@mui/icons-material/Create'
import {
	Box,
	Button,
	IconButton,
	TableCell,
	Typography
} from '@mui/material'
import { percent } from 'csx'
import { runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useEffect, useMemo } from 'react'
import { useHistory } from 'react-router'
import {
	BaseDataProviderInstanceModel,
	DataProviderConfiguration,
	DataProviderInstanceMetadata,
} from '../../../../api/clients/dataProviders/DTO'
import DataProviderInstancesClient from '../../../../api/clients/dataProviders/DataProviderInstancesClient'
import DataProvidersClient from '../../../../api/clients/dataProviders/DataProvidersClient'
import { fieldValue, UploadDialog } from '../../../../components/dialogs/UploadDialog'
import { FullscreenSpinner } from '../../../../components/feedback/circular'
import { TableColumn } from '../../../../components/tables/DTO'
import { FilteredTable } from '../../../../components/tables/FilteredTable'
import { FilterControl, SelectFilterOption } from '../../../../components/tables/filters/DTO'
import { useModals } from '../../../../services/notifications/ModalService'
import { usePrompts } from '../../../../services/notifications/PromptService'
import { toastService } from '../../../../services/notifications/ToastService'
import AdministrationPageContainer from '../AdministrationPageContainer'
import { CreateDataProviderInstanceDialog } from './CreateDataProviderInstanceDialog'
import { DeleteDataProviderDialog } from './DeleteDataProviderDialog'

export type InstanceIdAndName = {
	id: string
	name: string
}

export const DataProvidersDashboard = observer(() => {
	const history = useHistory()
	const modalService = useModals()
	const promptService = usePrompts()

	const dataProvidersClient = new DataProvidersClient()
	const instancesClient = new DataProviderInstancesClient()

	const localStore = useLocalObservable(() => ({
		dataProviders: [] as DataProviderConfiguration[],
		dataProviderInstances: [] as DataProviderInstanceMetadata[],
		requestsComplete: false
	}))

	const handleUploadClicked = () =>
		modalService.showForm((props) =>
			<UploadDialog<BaseDataProviderInstanceModel>
				uploadTypeName='Data Provider'
				acceptedFileExtensions='.fdp'
				additionalFields={[{
					name: 'Name',
					validationTests: [{
						isValid: (v) => localStore.dataProviderInstances.every(
							(instance) => instance.displayName !== v
						),
						invalidMessage: 'A data provider instance with this name already exists in your organization'
					}],
					getAutofillValue: (v) => v.displayName
				}]}
				onCancel={() => props.close({ closeResult: 'cancel' })}
				onConfirm={(data, fieldValues) => props.close({ closeResult: 'okay', value: { data: data, fieldValues: fieldValues } })}
			/>
		).then(async (v) => {
			const { data, fieldValues } = v.value as { data: BaseDataProviderInstanceModel, fieldValues?: Record<string, fieldValue> }
			if (v.closeResult === 'okay')
				await handleCreateInstance({
					...data,
					displayName: fieldValues?.Name ?? data.displayName
				})
		})

	const handleCreateClicked = () => {
		modalService
			.showForm((props) => {
				return (
					<CreateDataProviderInstanceDialog
						dataProviders={localStore.dataProviders}
						instanceNames={
							localStore.dataProviderInstances.map((v) => v.displayName) ?? []
						}
						onCancel={() => props.close({ closeResult: 'cancel' })}
						onConfirm={(v) =>
							props.close({ closeResult: 'okay', value: v })
						}
					/>
				)
			})
			.then(async (v) => {
				if (v.closeResult === 'okay')
					await handleCreateInstance(
						v.value as BaseDataProviderInstanceModel,
					)
			})
	}

	const handleEditClicked = (instanceId: string) => {
		history.push(`/_administration/_data-providers/${instanceId}`)
	}

	const handleDownloadClicked = (instanceId: string) => {
		instancesClient.getInstance(instanceId)
			.then((v) => {
				const data64 = window.btoa(JSON.stringify(v.data))

				const anchor = document.createElement('a', {})
				anchor.href = 'data:application/json;base64,' + data64
				anchor.target = '_blank'
				anchor.download = `dataProvider-${instanceId}.fdp`

				anchor.click()
				anchor.remove()
			})
	}

	const handleDeleteInstance = async (instanceId: string) => {
		instancesClient.deleteInstance(instanceId)

		const originalInstances = localStore.dataProviderInstances
		const newInstances = (originalInstances ?? []).filter(
			(v) => v.id !== instanceId,
		)

		runInAction(() => localStore.dataProviderInstances = newInstances)
	}

	const handleCreateInstance = async (
		baseModel: BaseDataProviderInstanceModel,
	) => {
		instancesClient
			.configure(baseModel)
			.then((response) => {
				const instanceId = response.data.id
				history.push(`/_administration/_data-providers/${instanceId}`)

				toastService.displayToast({
					message: 'Data provider created',
					area: 'global',
					severity: 'success',
				})
			})
			.catch(() => {
				toastService.displayToast({
					message: 'Error creating data provider',
					area: 'global',
					severity: 'error',
				})
			})
	}

	const getDataProviderTypes = async () => {
		const { data } = await dataProvidersClient.getDataProviders()
		runInAction(() => localStore.dataProviders = data)
	}

	const getDataProviderInstances = async () => {
		const { data } = await instancesClient.getMetadataForInstances()
		runInAction(() => localStore.dataProviderInstances = data)
	}

	useEffect(() => {
		Promise.all([getDataProviderTypes(), getDataProviderInstances()]).then(() =>
			runInAction(() => localStore.requestsComplete = true),
		)
	}, [])

	const getTypeOptions = useMemo((): SelectFilterOption[] => {
		const typeOptions: Record<string, unknown> = {}
		localStore.dataProviderInstances.forEach((instance) => typeOptions[instance.providerType] = true)
		return Object.keys(typeOptions).map((k) => ({
			label: k,
			value: k
		}))
	}, [localStore.dataProviderInstances])

	const filterControls: FilterControl[] = useMemo(() => [
		{
			type: 'multiselect',
			name: 'providerType',
			label: 'Type',
			options: getTypeOptions
		},
		{
			type: 'select',
			name: 'isConfigured',
			options: [{ label: "All Sources", value: "all" }, { label: "System", value: "internal" }, { label: "Configured", value: "configured" }],
			filterEntry: (entry, filterValue) =>
				filterValue === "all" ? true : (filterValue === "internal") === (entry as DataProviderInstanceMetadata).isInternal
		},
		{
			type: 'search',
			name: 'search',
			column: ["displayName", "description"]
		}
	], [localStore.dataProviderInstances])

	const columns: TableColumn<DataProviderInstanceMetadata>[] = useMemo(() => [
		{
			name: "displayName",
			label: "Name",
			renderContent: (instance) =>
				<Typography
					variant="inherit"
					color={instance.isMissingPlugin ? 'error' : ''}
				>
					{instance.displayName}
				</Typography>
		},
		{
			name: "providerType",
			label: "Type",
			renderContent: (instance) =>
				instance.isMissingPlugin ? (
					<Box display="flex" alignItems="center" gap={1}>
						<Warning color="error" fontSize="small" />
						<Typography variant="subtitle2" color="error">
							Plugin Missing
						</Typography>
					</Box>
				) : (
					instance.providerType
				)
		},
		{
			name: "description",
			label: "Description",
			renderContent: (instance) => instance.description.length < 100
				? instance.description
				: instance.description.substring(0, 99) + '...'
		},
		{
			renderHeading: <TableCell key={3} align="right">Actions</TableCell>,
			renderCell: (instance) =>
				<TableCell key={3} align="right">
					{instance.isInternal ? (
						<IconButton
							onClick={() => handleEditClicked(instance.id)}
						>
							<ArrowForward fontSize="small" />
						</IconButton>
					) : (
						<>
							{!instance.isMissingPlugin && (
								<IconButton
									onClick={() =>
										handleEditClicked(instance.id)
									}
								>
									<Edit fontSize="small" />
								</IconButton>
							)}
							<IconButton
								onClick={() => handleDownloadClicked(instance.id)}
							>
								<FileDownload />
							</IconButton>
							<IconButton
								onClick={() => {
									promptService
										.showDialog((props) => (
											<DeleteDataProviderDialog
												instance={{
													id: instance.id,
													name: instance.displayName,
												}}
												onCancel={() => props.close('cancel')}
												onConfirm={() => props.close('okay')}
											/>
										))
										.then((v) => {
											if (v == 'okay')
												handleDeleteInstance(instance.id)
										})
								}}
							>
								<Delete fontSize="small" />
							</IconButton>
						</>
					)}
				</TableCell>
		}
	], [])

	if (!localStore.requestsComplete) return <FullscreenSpinner />

	return (
		<AdministrationPageContainer
			title="Data Providers"
			actions={
				<Box
					display="flex"
					justifyContent={'center'}
					alignItems={'center'}
					gap={2}
				>
					<Button
						variant="outlined"
						hidden={localStore.dataProviders.length === 0}
						color="primary"
						onClick={handleUploadClicked}
						startIcon={<Upload />}
					>
						Upload Data Provider
					</Button>
					<Button
						variant="contained"
						hidden={localStore.dataProviders.length === 0}
						color="primary"
						onClick={handleCreateClicked}
						startIcon={<Create />}
					>
						Create Data Provider
					</Button>
				</Box>
			}
		>
			{localStore.dataProviderInstances.length === 0 && localStore.dataProviders.length === 0 ? (
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					width={percent(100)}
				>
					<Typography variant="subtitle1">
						No Data Provider Plugins Included
					</Typography>
				</Box>
			) : (
				<FilteredTable
					allEntries={localStore.dataProviderInstances}
					columns={columns}
					filterControls={filterControls}
				/>
			)}
		</AdministrationPageContainer>
	)
})
